import React, { useMemo, useState, useEffect } from 'react';
import Main from './main';
import './App.css';
import feathersStore from './feathersStore';
import { Provider, observer } from 'mobx-react';
import '@fontsource/roboto';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ColorModeContext, getStoredColorMode, setStoredColorMode, applyDarkClass } from './theme/colorMode';

// Importing Main (above) applies the constants.js theme class to <html>, and
// './App.css' injects the per-theme CSS variables — so they are resolvable here.
// Build ONE app-level MUI theme from those tokens (resolved to hex, since
// createTheme cannot parse a raw var()). This brands MUI surfaces — menus,
// inputs, date pickers, dialogs — and aligns their font to Geologica. It does
// NOT affect GreenSubmitButton, which keeps its own nested <ThemeProvider>.
const resolveHex = (varName, fallback) => {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    if (!raw) return fallback;
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = raw;
    return ctx.fillStyle;
  } catch (e) {
    return fallback;
  }
};

// MUI date/time pickers (checkout "schedule for later", reservation calendar) used to be pinned
// to adapterLocale="el" app-wide, so a UK shop rendered Greek month names and Greek date order.
// Derive it from the app's ACTIVE language instead — the same feathersStore.language that
// _useTranslate / main.js getLanguage drive. A Greek tenant still resolves to "el" exactly as
// today (the isGreekFiscal short-circuit also covers the pre-settings first paint, where the
// helper defaults to Greek), and en on a non-Greek tenant maps to en-GB so the UK gets dd/MM/yyyy.
const pickAdapterLocale = () => {
  if (feathersStore.isGreekFiscal()) return 'el';
  const lang = feathersStore.language || feathersStore.settings?.masterLanguage || 'en';
  if (lang === 'el') return 'el';
  if (lang === 'en') return 'en-GB';
  return lang;
};

const App = observer(function App() {
  // LIGHT/DARK axis: a manual choice (light/dark) overrides the OS; 'auto'
  // follows the OS prefers-color-scheme. useMediaQuery keeps 'auto' live if the
  // OS flips while the app is open. The effective scheme drives BOTH the CSS
  // `.dark-mode` class on <html> (which flips the neutral CSS tokens) and MUI's
  // neutral paper/bg/text. Brand primary/secondary stay branded in dark.
  const [mode, setMode] = useState(getStoredColorMode);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const isDark = mode === 'dark' || (mode === 'auto' && prefersDark);

  // Persist the choice and reflect it on <html> whenever the mode changes, or —
  // in 'auto' — when the OS preference changes.
  useEffect(() => {
    setStoredColorMode(mode);
    applyDarkClass(isDark);
  }, [mode, isDark]);

  const colorMode = useMemo(() => ({ mode, isDark, setMode }), [mode, isDark]);

  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary:   { main: resolveHex('--color-primary', '#e31c23') },
      secondary: { main: resolveHex('--color-accent', '#008f4a') },
      error:     { main: resolveHex('--color-danger', '#cd040b') },
      ...(isDark ? {
        background: { default: '#121212', paper: '#1e1e1e' },
        text: { primary: '#ececec', secondary: 'rgba(255,255,255,0.6)' },
      } : {}),
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: 'Geologica, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
  }), [isDark]);

  return (
    <ColorModeContext.Provider value={colorMode}>
    <ThemeProvider theme={muiTheme}>
    <GoogleOAuthProvider clientId={feathersStore.settings?.GOOGLE_CLIENT_ID ?? ''}>
    <Provider feathersStore={feathersStore}>
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={pickAdapterLocale()}>
    <PayPalScriptProvider options={{ clientId: feathersStore.settings?.PRODUCTION_CLIENT_ID ?? '', currency: feathersStore.settings?.currency ?? 'EUR', intent: 'capture' }} deferLoading={true}>
      <Main/>
    </PayPalScriptProvider>
     </LocalizationProvider>
    </Provider>
    </GoogleOAuthProvider>
    </ThemeProvider>
    </ColorModeContext.Provider>
  );
});

export default App;
